/**
 * Browser-side client for the BSO voice-relay bridge `/app` path.
 *
 * Mirrors the Android VoiceGrokClient (services/voice-relay/src/app-client.js
 * defines the wire protocol — keep the two clients in sync). PCM16 mono at
 * 24 kHz both directions; no μ-law transcode.
 *
 * Usage (inside a React component):
 *
 *   const client = new VoiceGrokBrowserClient({
 *     storeId,
 *     getToken,
 *     onEvent: (e) => { ... },
 *   });
 *   await client.start();         // requests mic, opens WS, starts streaming
 *   ...
 *   client.stop();                // graceful bye
 *
 * Modern browsers expose the microphone as Float32 samples at the
 * AudioContext's sample rate. We open the AudioContext at 24 kHz so we
 * don't have to resample (Chrome/Safari both support this), pull frames
 * via an AudioWorklet, and convert Float32 → Int16 inline. Inbound audio
 * is decoded base64 → Int16 → Float32 → AudioBuffer → AudioBufferSourceNode
 * playback queue so jitter doesn't introduce gaps.
 */

const BRIDGE_BASE_URL = (() => {
  const fromEnv = (process.env.REACT_APP_VOICE_RELAY_URL as string | undefined)?.trim();
  return (fromEnv && fromEnv.length > 0)
    ? fromEnv.replace(/\/$/, '')
    : 'wss://voice-relay.bigsystemone.com';
})();

const SAMPLE_RATE = 24_000;
const CHUNK_MS = 20;
const CHUNK_SAMPLES = (SAMPLE_RATE * CHUNK_MS) / 1000; // 480 samples per 20 ms

export type VoiceEvent =
  | { type: 'connecting' }
  | { type: 'ready'; sessionId: string }
  | { type: 'transcript'; role: 'user' | 'assistant'; text: string }
  | { type: 'tool_call'; id: string; name: string; arguments: Record<string, unknown> }
  | { type: 'assistant_speaking_start' }
  | { type: 'assistant_speaking_end' }
  | { type: 'error'; code: string; message: string }
  | { type: 'closed'; reason: string };

export interface VoiceGrokBrowserClientOptions {
  storeId: string;
  callSid?: string;
  voice?: string;
  systemPrompt?: string;
  /** Async getter for the Cognito ID token (used in the WS upgrade header — falls back to URL query param if browser blocks header). */
  getToken: () => Promise<string | null> | string | null;
  onEvent: (event: VoiceEvent) => void;
}

const WORKLET_SOURCE = `
class PCM16CaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0 || !input[0]) return true;
    // Mono — sum channels if needed, then convert to Int16 LE.
    const channel = input[0];
    const out = new Int16Array(channel.length);
    for (let i = 0; i < channel.length; i++) {
      let s = Math.max(-1, Math.min(1, channel[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    this.port.postMessage(out, [out.buffer]);
    return true;
  }
}
registerProcessor('pcm16-capture', PCM16CaptureProcessor);
`;

function int16ToBase64(int16: Int16Array): string {
  const bytes = new Uint8Array(int16.buffer, int16.byteOffset, int16.byteLength);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}

function base64ToInt16(b64: string): Int16Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  // Bytes are PCM16 LE. Construct a typed view that aliases the buffer
  // safely (copy because atob's buffer length may not be 2-aligned in
  // pathological cases).
  const aligned = bytes.byteLength % 2 === 0 ? bytes : bytes.subarray(0, bytes.byteLength - 1);
  return new Int16Array(aligned.buffer, aligned.byteOffset, aligned.byteLength / 2);
}

export class VoiceGrokBrowserClient {
  private ws: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private playbackHeadSec = 0;
  private running = false;
  private pendingCaptureBuffer: number[] = [];

  constructor(private opts: VoiceGrokBrowserClientOptions) {}

  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.opts.onEvent({ type: 'connecting' });

    let token: string | null = null;
    try {
      const t = this.opts.getToken();
      token = t instanceof Promise ? await t : t;
    } catch {
      token = null;
    }

    // Request mic first so we can fail early on permission denial — also
    // means the user-gesture-to-AudioContext-resume rule is satisfied.
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: SAMPLE_RATE,
        } as MediaTrackConstraints,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'permission denied';
      this.opts.onEvent({ type: 'error', code: 'mic_unavailable', message: msg });
      this.running = false;
      return;
    }

    // Browsers can't always honour sampleRate in getUserMedia, so we anchor
    // the AudioContext at 24 kHz which then resamples for us via the
    // worklet's input.
    const AC: typeof AudioContext = (window as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
      || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.audioCtx = new AC({ sampleRate: SAMPLE_RATE });

    // Register the worklet from an inline blob so we don't have to ship a
    // separate static asset.
    const blobUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'text/javascript' }));
    try {
      await this.audioCtx.audioWorklet.addModule(blobUrl);
    } catch (err) {
      this.opts.onEvent({ type: 'error', code: 'worklet_failed', message: (err as Error).message });
      this.cleanup();
      return;
    } finally {
      URL.revokeObjectURL(blobUrl);
    }

    const src = this.audioCtx.createMediaStreamSource(this.mediaStream);
    this.workletNode = new AudioWorkletNode(this.audioCtx, 'pcm16-capture');
    this.workletNode.port.onmessage = (e: MessageEvent<Int16Array>) =>
      this.handleCapturedSamples(e.data);
    src.connect(this.workletNode);

    // The worklet doesn't need to drive the destination, but Chrome will
    // suspend the graph if nothing is connected — feed a muted gain to
    // keep it scheduling.
    const muted = this.audioCtx.createGain();
    muted.gain.value = 0;
    this.workletNode.connect(muted).connect(this.audioCtx.destination);

    // WebSocket — try header first, fall back to query param if header
    // isn't honoured (browsers can't set arbitrary headers on WebSocket).
    const wsUrl = new URL(`${BRIDGE_BASE_URL}/app`);
    wsUrl.searchParams.set('storeId', this.opts.storeId);
    if (this.opts.callSid) wsUrl.searchParams.set('callSid', this.opts.callSid);
    if (token) wsUrl.searchParams.set('token', token);

    this.ws = new WebSocket(wsUrl.toString());
    this.ws.binaryType = 'arraybuffer';

    this.ws.addEventListener('open', () => {
      this.send({
        event: 'hello',
        storeId: this.opts.storeId,
        callSid: this.opts.callSid ?? null,
        sampleRate: SAMPLE_RATE,
        voice: this.opts.voice ?? 'human_Ara',
        ...(this.opts.systemPrompt ? { systemPrompt: this.opts.systemPrompt } : {}),
      });
    });

    this.ws.addEventListener('message', (msg) => this.handleServerMessage(msg.data));
    this.ws.addEventListener('error', () =>
      this.opts.onEvent({ type: 'error', code: 'ws_error', message: 'WebSocket error' })
    );
    this.ws.addEventListener('close', (e) => {
      this.opts.onEvent({ type: 'closed', reason: `code=${e.code}` });
      this.cleanup();
    });
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    try { this.ws?.send(JSON.stringify({ event: 'bye' })); } catch { /* noop */ }
    try { this.ws?.close(1000, 'client_stop'); } catch { /* noop */ }
    this.cleanup();
  }

  /** Forward text to xAI without going through audio (fallback for keyboard input). */
  sendText(text: string): void {
    this.send({ event: 'text', text });
  }

  /** Reply to a server-initiated tool call. */
  sendToolResult(id: string, result?: unknown, error?: string): void {
    const payload: Record<string, unknown> = { event: 'tool_result', id };
    if (result !== undefined) payload.result = result;
    if (error !== undefined) payload.error = error;
    this.send(payload);
  }

  private send(obj: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(obj));
  }

  private handleCapturedSamples(samples: Int16Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Not connected yet — buffer up to ~1s of audio.
      if (this.pendingCaptureBuffer.length < SAMPLE_RATE) {
        for (let i = 0; i < samples.length; i++) this.pendingCaptureBuffer.push(samples[i]);
      }
      return;
    }
    if (this.pendingCaptureBuffer.length > 0) {
      const flushed = new Int16Array(this.pendingCaptureBuffer);
      this.pendingCaptureBuffer = [];
      this.send({ event: 'audio', data: int16ToBase64(flushed) });
    }
    // Slice into 20ms chunks so the bridge sees consistent framing.
    let offset = 0;
    while (offset + CHUNK_SAMPLES <= samples.length) {
      const slice = samples.subarray(offset, offset + CHUNK_SAMPLES);
      this.send({ event: 'audio', data: int16ToBase64(slice) });
      offset += CHUNK_SAMPLES;
    }
    if (offset < samples.length) {
      // Leftover partial chunk — buffer for next callback so we don't ship
      // odd-sized frames.
      const remainder = samples.subarray(offset);
      for (let i = 0; i < remainder.length; i++) this.pendingCaptureBuffer.push(remainder[i]);
    }
  }

  private handleServerMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let msg: Record<string, unknown>;
    try { msg = JSON.parse(raw); } catch { return; }
    switch (msg.event) {
      case 'ready':
        this.opts.onEvent({ type: 'ready', sessionId: String(msg.sessionId || '') });
        break;
      case 'audio':
        if (typeof msg.data === 'string') this.queuePlayback(msg.data);
        break;
      case 'audio_end':
        this.opts.onEvent({ type: 'assistant_speaking_end' });
        break;
      case 'transcript':
        this.opts.onEvent({
          type: 'transcript',
          role: (msg.role as 'user' | 'assistant') || 'assistant',
          text: String(msg.text || '')
        });
        break;
      case 'tool_call':
        this.opts.onEvent({
          type: 'tool_call',
          id: String(msg.id || ''),
          name: String(msg.name || ''),
          arguments: (msg.arguments as Record<string, unknown>) || {}
        });
        break;
      case 'error':
        this.opts.onEvent({
          type: 'error',
          code: String(msg.code || 'error'),
          message: String(msg.message || '')
        });
        break;
      case 'closed':
        this.opts.onEvent({ type: 'closed', reason: String(msg.reason || 'server_close') });
        break;
    }
  }

  private queuePlayback(b64: string): void {
    if (!this.audioCtx) return;
    const int16 = base64ToInt16(b64);
    if (int16.length === 0) return;
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 0x8000;

    const buffer = this.audioCtx.createBuffer(1, float32.length, SAMPLE_RATE);
    buffer.copyToChannel(float32, 0);
    const source = this.audioCtx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioCtx.destination);
    const startAt = Math.max(this.audioCtx.currentTime, this.playbackHeadSec);
    source.start(startAt);
    this.playbackHeadSec = startAt + buffer.duration;
    this.opts.onEvent({ type: 'assistant_speaking_start' });
  }

  private cleanup(): void {
    try { this.workletNode?.port.close(); } catch { /* noop */ }
    try { this.workletNode?.disconnect(); } catch { /* noop */ }
    this.workletNode = null;
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
    this.audioCtx?.close().catch(() => { /* noop */ });
    this.audioCtx = null;
    this.playbackHeadSec = 0;
    this.pendingCaptureBuffer = [];
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      try { this.ws.close(); } catch { /* noop */ }
    }
    this.ws = null;
  }
}
